<script setup lang="ts">
// #737: 利用の流れ（/ 用、旧 HowToSection.vue の後継）。
// 「1（無料登録）だけが必須の最初 → あとは順不同の並列3ルート」という分岐構造にする。
// 直列の番号（1→2→3→4）にすると順序が固定されているように誤読されるため、
// STEP 1 のあとは番号なしの並列カードで「どれからでも」を表現する。
import { mdiGamepadVariant, mdiCompassOutline, mdiFeather, mdiArrowRight } from '@mdi/js'

// 並び順は料金セクション（PricingSection）と揃える（遊ぶ→つくる→学ぶ）。
// 無料→無料→有料（エンコーディア探索）のグラデーションで並列3ルートを終える。#737
const paths = [
  {
    icon: mdiGamepadVariant,
    title: 'みんなの作品で、遊ぶ',
    place: '魔法研究会',
    desc: '集まった作品を、ブラウザですぐプレイ。あそぶだけでも十分に楽しめます。',
  },
  {
    icon: mdiFeather,
    title: '作って、公開する',
    place: '羽根筆のゆりかご',
    desc: '思いついたゲームを自由に創作。魔法研究会で公開して、世界中の人に遊んでもらえます。',
  },
  {
    icon: mdiCompassOutline,
    title: '写して、学ぶ',
    place: 'エンコーディア探索',
    desc: '世界を冒険して紙の魔導書を手に入れ、写しながら身につける。女神（AI）が支えてくれます。',
  },
]

const { trackCta } = useCtaEvent()
</script>

<template>
  <div class="centered-area">
    <div class="howto-card">
      <h2 class="howto-title">
        利用の流れ
      </h2>

      <!-- Step 1: 唯一の「最初」。右に登録CTAを置いて左右のバランスを取る -->
      <div class="first-step">
        <div class="first-main">
          <div class="step-num">
            1
          </div>
          <div class="first-body">
            <h3 class="step-title">
              まずは無料で登録
            </h3>
            <p class="step-desc">
              メールアドレスだけで、すぐに始められます。もちろん無料です。
            </p>
          </div>
        </div>
        <NuxtLink
          to="/signup"
          class="first-cta"
          @click="trackCta('冒険を始める（howto）')"
        >
          冒険を始める
          <span class="first-cta-free">無料</span>
        </NuxtLink>
      </div>

      <!-- 順不同を示すディバイダ -->
      <div class="anyorder">
        <span>あとは、どれからでも</span>
      </div>

      <!-- 並列3ルート（順不同・番号なし） -->
      <div class="paths">
        <div
          v-for="p in paths"
          :key="p.place"
          class="path-card"
        >
          <v-icon
            :icon="p.icon"
            size="40"
            class="path-icon"
          />
          <h4 class="path-title">
            {{ p.title }}
          </h4>
          <div class="path-place">
            {{ p.place }}
          </div>
          <p class="path-desc">
            {{ p.desc }}
          </p>
        </div>
      </div>

      <div class="howto-more">
        <NuxtLink
          to="/articles"
          class="more-link"
        >
          詳しくは「エンコーディア通信」へ
          <v-icon
            :icon="mdiArrowRight"
            size="18"
          />
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<style scoped>
.centered-area {
  width: 100%;
  max-width: var(--landing-max-width);
  margin: auto;
  padding: var(--landing-gutter);
}

.howto-card {
  max-width: 1080px;
  margin: 0 auto;
  background: #fff;
  border-radius: var(--landing-radius);
  box-shadow: var(--landing-shadow);
  padding: clamp(26px, 4vw, 48px) clamp(20px, 4vw, 48px);
  color: var(--landing-color-text-dark);
}

.howto-title {
  font-size: var(--landing-h2-size);
  font-weight: 700;
  text-align: center;
  margin: 0 0 clamp(20px, 3.5vw, 30px);
}

.howto-lead {
  text-align: center;
  font-weight: 500;
  color: var(--landing-color-teal-darker);
  margin: 0 0 clamp(22px, 4vw, 34px);
}

/* ===== STEP 1（唯一の最初） ===== */
.first-step {
  display: flex;
  flex-wrap: wrap;
  gap: 16px 28px;
  align-items: center;
  justify-content: space-between;
  background: var(--landing-color-card-mint);
  border: 1px solid var(--landing-color-border-mint);
  border-radius: var(--landing-radius);
  padding: clamp(18px, 2.6vw, 26px);
}

.first-main {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  flex: 1 1 auto;
  min-width: 0;
}

.first-cta {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  color: #1a1510;
  background: linear-gradient(135deg, #d9a83e, #f0c96a);
  border-radius: 999px;
  padding: 12px 30px;
  font-weight: 700;
  letter-spacing: 0.02em;
  box-shadow: 0 8px 26px -8px rgba(216, 160, 60, 0.7);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.first-cta:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 32px -8px rgba(216, 160, 60, 0.8);
}

.first-cta-free {
  font-size: 0.7em;
  font-weight: 800;
  color: #fff;
  background: #1a7566;
  border-radius: 999px;
  padding: 2px 10px;
}

@media (max-width: 600px) {
  .first-cta {
    width: 100%;
    justify-content: center;
  }
}

.step-num {
  flex: none;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--landing-color-teal-dark), var(--landing-color-teal));
  color: #fff;
  display: grid;
  place-items: center;
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 800;
  font-size: 1.2rem;
  box-shadow: 0 3px 10px rgba(47, 161, 146, 0.4);
}

.first-body {
  padding-top: 3px;
}

.step-title {
  font-family: 'Noto Sans JP', sans-serif;
  font-size: clamp(1.05rem, 1.6vw, 1.3rem);
  font-weight: 700;
  margin: 0 0 6px;
}

.step-desc {
  margin: 0;
  font-size: clamp(0.95rem, 1.1vw, 1.1rem);
  line-height: 1.7;
  color: #4a4a4a;
}

/* ===== 順不同ディバイダ ===== */
.anyorder {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: clamp(18px, 3vw, 26px) 4px;
}

.anyorder::before,
.anyorder::after {
  content: "";
  flex: 1;
  height: 1px;
  background: var(--landing-color-border-mint);
}

.anyorder span {
  font-weight: 700;
  font-size: 0.95rem;
  color: var(--landing-color-teal-darker);
  white-space: nowrap;
}

/* ===== 並列3ルート ===== */
.paths {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
}

.path-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  background: #fff;
  border: 1px solid var(--landing-color-border-mint);
  border-radius: var(--landing-radius);
  padding: clamp(18px, 2.6vw, 24px);
  text-align: center;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.path-card:hover {
  box-shadow: var(--landing-shadow-light);
  transform: translateY(-2px);
}

.path-icon {
  color: var(--landing-color-teal);
}

.path-title {
  font-family: 'Noto Sans JP', sans-serif;
  font-size: 1.1rem;
  font-weight: 700;
  margin: 2px 0 0;
}

.path-place {
  font-size: 0.8rem;
  font-weight: 700;
  color: #fff;
  /* AA contrast (≥4.5) を白文字に対して満たす濃いティール (#1A7566)。
     旧 --landing-color-teal (#3EB6A6) は白文字とのコントラストが不足していた (#737 Lighthouse)。 */
  background: var(--landing-color-teal-darker);
  border-radius: 999px;
  padding: 2px 12px;
}

.path-desc {
  margin: 4px 0 0;
  font-size: 0.92rem;
  line-height: 1.7;
  color: #4a4a4a;
}

.howto-more {
  margin-top: clamp(24px, 4vw, 36px);
  text-align: center;
}

.more-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  white-space: nowrap;
  color: var(--landing-color-teal-darker);
  text-decoration: none;
  border: 1px solid var(--landing-color-teal);
  border-radius: 999px;
  padding: 10px 22px;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.more-link:hover {
  background: var(--landing-color-teal);
  color: #fff;
}

@media (min-width: 600.02px) {
  .paths {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }
}
</style>
